// Template key points for each pattern, mirrored from stocks_api/patterns/templates.py
// Format: [position_fraction (0-1), height_fraction (0-1)]
// position: 0 = start, 1 = end
// height: 0 = price low, 1 = price high
// invert: true means the pattern is the mirror image of the base points (price low <-> high)

type KeyPoint = [number, number];

export interface PatternTemplate {
  points: KeyPoint[];
  invert?: boolean;
  sentiment: 'bullish' | 'bearish';
}

export const PATTERN_TEMPLATES: Record<string, PatternTemplate> = {
  head_shoulders: {
    sentiment: 'bearish',
    points: [
      [0.00, 0.00], [0.10, 0.25], [0.15, 0.40], [0.25, 0.10],
      [0.38, 0.50], [0.50, 0.70], [0.62, 0.50], [0.75, 0.10],
      [0.80, 0.25], [0.85, 0.35], [1.00, 0.00],
    ],
  },
  inv_head_shoulders: {
    sentiment: 'bullish',
    invert: true,
    points: [
      [0.00, 0.00], [0.10, 0.25], [0.15, 0.40], [0.25, 0.10],
      [0.38, 0.50], [0.50, 0.70], [0.62, 0.50], [0.75, 0.10],
      [0.80, 0.25], [0.85, 0.35], [1.00, 0.00],
    ],
  },
  double_top: {
    sentiment: 'bearish',
    points: [
      [0.00, 0.00], [0.12, 0.60], [0.25, 1.00],
      [0.38, 0.60], [0.50, 0.30],
      [0.62, 0.60], [0.75, 0.95],
      [0.88, 0.50], [1.00, 0.00],
    ],
  },
  double_bottom: {
    sentiment: 'bullish',
    invert: true,
    points: [
      [0.00, 0.00], [0.12, 0.60], [0.25, 1.00],
      [0.38, 0.60], [0.50, 0.30],
      [0.62, 0.60], [0.75, 0.95],
      [0.88, 0.50], [1.00, 0.00],
    ],
  },
  triple_top: {
    sentiment: 'bearish',
    points: [
      [0.00, 0.00], [0.08, 0.50], [0.17, 0.90],
      [0.25, 0.50], [0.33, 0.15],
      [0.42, 0.50], [0.50, 0.87],
      [0.58, 0.50], [0.67, 0.15],
      [0.75, 0.50], [0.83, 0.85],
      [0.92, 0.30], [1.00, 0.00],
    ],
  },
  triple_bottom: {
    sentiment: 'bullish',
    invert: true,
    points: [
      [0.00, 0.00], [0.08, 0.50], [0.17, 0.90],
      [0.25, 0.50], [0.33, 0.15],
      [0.42, 0.50], [0.50, 0.87],
      [0.58, 0.50], [0.67, 0.15],
      [0.75, 0.50], [0.83, 0.85],
      [0.92, 0.30], [1.00, 0.00],
    ],
  },
  cup_handle: {
    sentiment: 'bullish',
    points: [
      [0.00, 1.00], [0.10, 0.50], [0.15, 0.30], [0.30, 0.10],
      [0.50, 0.00],
      [0.65, 0.10], [0.80, 0.30], [0.82, 0.50], [0.85, 0.90],
      [0.93, 0.70], [1.00, 1.00],
    ],
  },
  inv_cup_handle: {
    sentiment: 'bearish',
    invert: true,
    points: [
      [0.00, 1.00], [0.10, 0.50], [0.15, 0.30], [0.30, 0.10],
      [0.50, 0.00],
      [0.65, 0.10], [0.80, 0.30], [0.82, 0.50], [0.85, 0.90],
      [0.93, 0.70], [1.00, 1.00],
    ],
  },
  rounding_bottom: {
    sentiment: 'bullish',
    points: [
      [0.00, 1.00], [0.08, 0.84], [0.17, 0.68], [0.25, 0.52], [0.33, 0.36], [0.42, 0.22],
      [0.50, 0.10],
      [0.58, 0.22], [0.67, 0.36], [0.75, 0.52], [0.83, 0.68], [0.92, 0.84],
      [1.00, 1.00],
    ],
  },
  rounding_top: {
    sentiment: 'bearish',
    invert: true,
    points: [
      [0.00, 1.00], [0.08, 0.84], [0.17, 0.68], [0.25, 0.52], [0.33, 0.36], [0.42, 0.22],
      [0.50, 0.10],
      [0.58, 0.22], [0.67, 0.36], [0.75, 0.52], [0.83, 0.68], [0.92, 0.84],
      [1.00, 1.00],
    ],
  },
  ascending_triangle: {
    sentiment: 'bullish',
    points: [
      [0.00, 0.05], [0.18, 0.85], [0.30, 0.20],
      [0.45, 0.88], [0.58, 0.40],
      [0.75, 0.88], [1.00, 0.62],
    ],
  },
  descending_triangle: {
    sentiment: 'bearish',
    invert: true,
    points: [
      [0.00, 0.05], [0.18, 0.85], [0.30, 0.20],
      [0.45, 0.88], [0.58, 0.40],
      [0.75, 0.88], [1.00, 0.62],
    ],
  },
  symmetrical_triangle: {
    sentiment: 'bullish',
    points: [
      [0.00, 0.50], [0.12, 0.95], [0.20, 0.55], [0.30, 0.12], [0.38, 0.48],
      [0.47, 0.75], [0.55, 0.52], [0.63, 0.28], [0.72, 0.50],
      [0.80, 0.62], [0.90, 0.50], [1.00, 0.42],
    ],
  },
  rising_wedge: {
    sentiment: 'bearish',
    points: [
      [0.00, 0.00], [0.12, 0.35], [0.22, 0.55], [0.30, 0.38], [0.38, 0.30],
      [0.48, 0.52], [0.58, 0.65], [0.65, 0.52], [0.72, 0.50],
      [0.82, 0.63], [1.00, 0.70],
    ],
  },
  falling_wedge: {
    sentiment: 'bullish',
    invert: true,
    points: [
      [0.00, 0.00], [0.12, 0.35], [0.22, 0.55], [0.30, 0.38], [0.38, 0.30],
      [0.48, 0.52], [0.58, 0.65], [0.65, 0.52], [0.72, 0.50],
      [0.82, 0.63], [1.00, 0.70],
    ],
  },
  bull_flag: {
    sentiment: 'bullish',
    points: [
      [0.00, 0.00], [0.10, 0.40], [0.20, 0.72], [0.30, 1.00],
      [0.42, 0.88], [0.52, 0.76], [0.62, 0.82], [0.72, 0.72], [0.82, 0.76], [1.00, 0.65],
    ],
  },
  bear_flag: {
    sentiment: 'bearish',
    invert: true,
    points: [
      [0.00, 0.00], [0.10, 0.40], [0.20, 0.72], [0.30, 1.00],
      [0.42, 0.88], [0.52, 0.76], [0.62, 0.82], [0.72, 0.72], [0.82, 0.76], [1.00, 0.65],
    ],
  },
  bull_pennant: {
    sentiment: 'bullish',
    points: [
      [0.00, 0.00], [0.10, 0.40], [0.20, 0.72], [0.30, 1.00],
      [0.38, 0.90], [0.47, 0.85], [0.55, 0.72], [0.63, 0.68],
      [0.72, 0.78], [0.80, 0.80], [0.90, 0.74], [1.00, 0.76],
    ],
  },
  bear_pennant: {
    sentiment: 'bearish',
    invert: true,
    points: [
      [0.00, 0.00], [0.10, 0.40], [0.20, 0.72], [0.30, 1.00],
      [0.38, 0.90], [0.47, 0.85], [0.55, 0.72], [0.63, 0.68],
      [0.72, 0.78], [0.80, 0.80], [0.90, 0.74], [1.00, 0.76],
    ],
  },
  rectangle_bull: {
    sentiment: 'bullish',
    points: [
      [0.00, 0.50], [0.08, 0.75], [0.18, 0.90], [0.27, 0.65], [0.36, 0.10],
      [0.46, 0.55], [0.55, 0.90], [0.64, 0.65], [0.72, 0.10],
      [0.82, 0.55], [1.00, 0.90],
    ],
  },
  rectangle_bear: {
    sentiment: 'bearish',
    invert: true,
    points: [
      [0.00, 0.50], [0.08, 0.75], [0.18, 0.90], [0.27, 0.65], [0.36, 0.10],
      [0.46, 0.55], [0.55, 0.90], [0.64, 0.65], [0.72, 0.10],
      [0.82, 0.55], [1.00, 0.90],
    ],
  },
  broadening_formation: {
    sentiment: 'bearish',
    points: [
      [0.00, 0.50], [0.07, 0.62], [0.15, 0.70], [0.22, 0.52], [0.30, 0.25],
      [0.40, 0.55], [0.50, 0.88], [0.57, 0.52], [0.65, 0.08],
      [0.72, 0.48], [0.80, 1.00], [0.90, 0.42], [1.00, 0.00],
    ],
  },
};
